import { useMemo } from "react";

import type { ResolvedLeague } from "@/contexts";
import { useAuditLogPermissions, useCurrentLeagueTourType, useLeagueCapabilities } from "@/hooks";
import { hasCapability } from "@/utils/capability";

import { LeagueNavItem, useLeagueNavGroups } from "./useLeagueNavGroups";

const RANKINGS_ITEM_TYPES = new Set(["rankings", "rankingCategories", "rankingPoints"]);
const FACT_SHEET_ITEM_TYPES = new Set(["factSheet"]);
const LEAGUE_ONLY_ITEM_TYPES = new Set(["leagueSetup"]);

const isLeagueNavItemVisible = (
  item: LeagueNavItem,
  league: ResolvedLeague,
  isLeague: boolean
): boolean => {
  if (!league.enableRankings && RANKINGS_ITEM_TYPES.has(item.type)) return false;
  if (!league.enableFactSheet && FACT_SHEET_ITEM_TYPES.has(item.type)) return false;
  if (!isLeague && LEAGUE_ONLY_ITEM_TYPES.has(item.type)) return false;
  return true;
};

/**
 * Single permission/module-filtered league navigation model shared by the
 * sidebar and global search. Keeping the filtering here prevents search from
 * exposing a page that the active league sidebar intentionally hides.
 */
export const useVisibleLeagueNavGroups = (league: ResolvedLeague | null) => {
  const leagueNavGroups = useLeagueNavGroups();
  const { isLeague } = useCurrentLeagueTourType();
  const leagueCapabilities = useLeagueCapabilities();
  const { canSeeAuditLogs } = useAuditLogPermissions();

  return useMemo(() => {
    if (!league) return [];

    const hasLeagueNavItemAccess = (item: LeagueNavItem): boolean => {
      if (item.type === "auditLog") return canSeeAuditLogs("league");
      if (!item.capability) return true;
      return hasCapability(leagueCapabilities, item.capability);
    };

    return leagueNavGroups.map((group) => ({
      ...group,
      items: group.items.filter(
        (item) => isLeagueNavItemVisible(item, league, isLeague) && hasLeagueNavItemAccess(item)
      ),
    }));
  }, [canSeeAuditLogs, isLeague, league, leagueCapabilities, leagueNavGroups]);
};
